import { z } from 'zod'
import { RuleId, SectionId, FieldId } from './id.js'
import { Text } from './text.js'
import { PATTERN_MAX_EXAMPLES, PATTERN_MAX_INPUT, PATTERN_MAX_LENGTH, patternProblem } from './pattern.js'

/**
 * Een regel is data en nooit uitvoerbare code (R-01e): geen scripts, geen
 * Power Fx, geen eval. Wat een regel doet zit in de evaluator (M1); de definitie
 * beschrijft alleen wat er geldt. Meer dan dit is een regel niet: soort, doel,
 * parameters, mechanisme, melding (ontwerp 5.8).
 *
 * De lijst soorten is vast en bewust ruim (U15, D-14): de inrichter kiest per
 * veld een soort en vult parameters in, meer niet. Uitbreiden is een
 * engine-wijziging, en een nieuwe soort komt pas in een formulier terecht bij
 * een nieuwe publicatie (R-22a, B2).
 *
 * Drie dingen die hier met opzet niet staan:
 *
 * - De standaardmelding per soort. Die levert de engine (ontwerp 5.7), dus M1.
 * - Het standaardmechanisme per soort. Dat is een designtime-gemak (5.3), en
 *   D-15 laat de inrichter het per regel omzetten; het schema pint het dus niet
 *   vast en vraagt `mechanisme` altijd expliciet.
 * - Wie een soort mag vastleggen. Dat is autorisatie in de configurator, geen
 *   schemavorm; sinds 1 november 2026 heeft elke inrichter met Wijzigen de
 *   regex-escape, onder Geavanceerd (D-17, eisenboek 12.43).
 */

/**
 * Twee mechanismen, geen twee ernstniveaus (R-23, R-28). Hard is een poort op
 * indienen, waarschuwing is advies en blokkeert nooit.
 */
export const Mechanism = z.enum(['hard', 'waarschuwing'])
export type Mechanism = z.infer<typeof Mechanism>

/** Wat elke controle draagt, ongeacht de soort (ontwerp 5.8, ControleBasis). */
const base = {
  id: RuleId,
  mechanisme: Mechanism,
  /** Overschrijft de standaardmelding van de engine, in beide talen (R-30, D-16). */
  melding: Text.optional(),
}

/** Een controle op precies één veld (besluit bij R-29, ontwerp 5.4). */
const onField = { ...base, doelVeld: FieldId }

/**
 * Een controle op een hele sectie.
 *
 * Dit wijkt af van de letterlijke tekst van het besluit bij R-29 ("een controle
 * gaat over precies één veld") en volgt B11 en ontwerp 5.3, die "opvallend korte
 * sectie" als waarschuwingssoort noemen; een sectie is daar het onderwerp en
 * geen veld. De reden achter R-29 blijft overeind: dat besluit voorkomt een
 * expressietaal over meerdere velden, en deze soort heeft er geen nodig, want
 * hij leest één sectie. De afwijking staat hier expliciet zodat ze een besluit
 * is en geen ongeluk.
 */
const onSection = { ...base, doelSectie: SectionId }

const positiveInt = z.number().int().positive()

/**
 * Een patroon in de taal van `pattern.ts` (eisenboek 12.43): ten hoogste 200 tekens, en alleen wat de
 * lineaire matcher van de engine kent. De lengte staat ook als `max`, zodat het JSON Schema haar draagt; de
 * rest is een `superRefine` en overleeft de generatie niet (zie `jsonschema.ts`).
 */
export const PatternExpression = z
  .string()
  .min(1, 'het patroon is leeg')
  .max(PATTERN_MAX_LENGTH, `een patroon is ten hoogste ${String(PATTERN_MAX_LENGTH)} tekens`)
  .superRefine((expression, ctx) => {
    if (expression.length > PATTERN_MAX_LENGTH) return
    const problem = patternProblem(expression)
    if (problem !== undefined) ctx.addIssue({ code: 'custom', message: problem })
  })

/**
 * De voorbeelden bij een patroon: wat moet passen en wat niet (D-19, eisenboek 12.43).
 *
 * Ze staan in de regel zodat wie het formulier later opent ziet waarvoor het patroon bedoeld is, en zodat de
 * poort het bewijs kan narekenen. Of ze er zijn en kloppen toetst de poort (`patternExampleProblems` in de
 * engine), niet het schema: een concept met een half ingevuld patroon moet bewaard kunnen worden, en de
 * opslagdienst leest een bewaarde publicatie met dit schema (D-36).
 */
const example = z
  .string()
  .min(1, 'een voorbeeld is niet leeg')
  .max(PATTERN_MAX_INPUT, `een voorbeeld is ten hoogste ${String(PATTERN_MAX_INPUT)} tekens`)
const examples = z.array(example).max(PATTERN_MAX_EXAMPLES, `ten hoogste ${String(PATTERN_MAX_EXAMPLES)} voorbeelden per kant`)
export const PatternExamples = z.strictObject({ passend: examples, nietPassend: examples })
export type PatternExamples = z.infer<typeof PatternExamples>
const date = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'verwacht een datum als jjjj-mm-dd')

/**
 * De twaalf soorten die B11 als harde controle opsomt, plus `geheelGetal` (besluit van 27 oktober
 * 2026).
 */
const HARD = [
  /** Verplicht is een regelsoort met gedrag, geen losse vlag (R-26). */
  z.strictObject({ ...onField, soort: z.literal('verplicht') }),
  z.strictObject({ ...onField, soort: z.literal('maximumTekens'), maximum: positiveInt }),
  z.strictObject({ ...onField, soort: z.literal('maximumWoorden'), maximum: positiveInt }),
  z.strictObject({ ...onField, soort: z.literal('minimumWoorden'), minimum: positiveInt }),
  z
    .strictObject({
      ...onField,
      soort: z.literal('getalsbereik'),
      minimum: z.number().optional(),
      maximum: z.number().optional(),
    })
    .refine((r) => r.minimum !== undefined || r.maximum !== undefined, {
      error: 'een getalsbereik heeft een minimum, een maximum of beide',
    })
    .refine((r) => r.minimum === undefined || r.maximum === undefined || r.minimum <= r.maximum, {
      error: 'het minimum van een getalsbereik ligt niet boven het maximum',
    }),
  /**
   * Het getal is geheel: geen decimalen (besluit van 27 oktober 2026, uitbreiding van B11).
   *
   * Een eigen soort en geen parameter van `getalsbereik`, want het is een andere vraag: een bereik
   * gaat over de grootte, dit over de vorm. Een veld van het processysteem met kolomtype `geheel-getal`
   * krijgt deze regel bij het plaatsen, want een kolom met een geheel getal in Dataverse kan 2,5 niet
   * bewaren; de publicatiepoort eist hem daar als harde controle, en de opslagdienst weigert een
   * aangeleverde waarde met decimalen voor zo'n veld. Op een gewoon getalveld kiest de inrichter hem zelf.
   */
  z.strictObject({ ...onField, soort: z.literal('geheelGetal') }),
  z
    .strictObject({
      ...onField,
      soort: z.literal('datumbereik'),
      vroegste: date.optional(),
      laatste: date.optional(),
    })
    .refine((r) => r.vroegste !== undefined || r.laatste !== undefined, {
      error: 'een datumbereik heeft een vroegste, een laatste of beide',
    })
    .refine((r) => r.vroegste === undefined || r.laatste === undefined || r.vroegste <= r.laatste, {
      error: 'de vroegste datum ligt niet na de laatste',
    }),
  z.strictObject({
    ...onField,
    soort: z.literal('identifierFormaat'),
    formaat: z.enum(['doi', 'orcid', 'url', 'email', 'telefoon']),
  }),
  /** Het akkoordveld staat op Ja: daarmee is het akkoord een poort op indienen (R-19, R-89). */
  z.strictObject({ ...onField, soort: z.literal('akkoordJa') }),
  /**
   * Het aantal gaat over één ding, het aantal, en niet over de waarden erin
   * (R-42, ontwerp 5.4). Het werkt op elk veld waarvan de waarde een verzameling
   * is: rijen van een herhaalgroep, trefwoorden van een tag, gekozen opties van
   * een meerkeuzeveld of een aanvinklijst. Welke controls dat zijn staat in
   * `MULTI_VALUED` in veld.ts, en de definitie toetst het doel daaraan.
   */
  z.strictObject({ ...onField, soort: z.literal('minimumAantal'), minimum: positiveInt }),
  z.strictObject({ ...onField, soort: z.literal('maximumAantal'), maximum: positiveInt }),
  /**
   * Bestandseisen per bijlageveld (R-104). Alle drie de parameters zijn
   * verplicht: een bijlageveld zonder grens is een permissieve standaardwaarde,
   * en die bestaan hier niet (ontwerp 11.4). De schrijfgrens van de pod staat
   * daar los naast (9.2, regel 3).
   */
  z.strictObject({
    ...onField,
    soort: z.literal('bestandseisen'),
    typen: z.array(z.string().regex(/^[a-z0-9]{1,10}$/, 'een bestandstype is een extensie zonder punt')).min(1),
    maximumAantal: positiveInt,
    maximumGrootteMb: z.number().positive(),
  }),
  /**
   * De regex-escape gaat alleen over tekstvorm (U15, D-17). Sinds 1 november 2026 heeft elke inrichter
   * hem, onder Geavanceerd (eisenboek 12.43). Het schema toetst dat de expressie een patroon is in de taal
   * van `pattern.ts`: een expressie die dat niet is, is een gebroken regel en hoort niet in een definitie
   * te kunnen landen (D-21). De begrensde uitvoeringstijd uit ontwerp 5.3 volgt uit die taal: de engine
   * rekent een patroon lineair in de lengte van de invoer.
   */
  z.strictObject({
    ...onField,
    soort: z.literal('regexVorm'),
    expressie: PatternExpression,
    voorbeelden: PatternExamples.optional(),
  }),
] as const

/**
 * De waarschuwingssoorten uit B11 (R-28).
 *
 * Het zijn er twee en niet drie. "Vereist onderdeel lijkt te ontbreken" is op
 * 1 september 2026 laten vallen: B11 geeft die soort als parameter een
 * omschrijving van het onderdeel, en daaruit kan de evaluator niet opmaken
 * wanneer de regel faalt. Herkennen op een lijst termen zou kunnen, maar dat is
 * te grof om er een eis op te baseren; het onderwerp komt terug wanneer er een
 * assistent is die inhoud kan lezen (D-18, niet bij de start). Zie eisenboek 12.8.
 */
const WARNING = [
  z.strictObject({
    ...onField,
    soort: z.literal('verbodenTermen'),
    termen: z.array(z.string().min(1)).min(1),
  }),
  /** Het onderwerp is een sectie; zie de toelichting bij `onSection`. */
  z.strictObject({ ...onSection, soort: z.literal('korteSectie'), drempelWoorden: positiveInt }),
] as const

/**
 * De zichtbaarheidsconditie (R-33, klasse A).
 *
 * Geen mechanisme: zichtbaarheid is geen controle en kent die keuze niet
 * (ontwerp 5.3). Geen melding, om dezelfde reden. Het doel is een veld of een
 * hele sectie, want R-33 luidt letterlijk "een sectie of veld kan zichtbaar
 * worden"; dat is de reden dat een regel zijn doel expliciet noemt in plaats van
 * genest onder een veld te staan.
 *
 * Zichtbaarheid op grond van autorisatie of anonimisering hoort hier niet: die
 * is klasse B en zit in de projectiestap op de server (R-38, R-01d).
 */
const VISIBILITY = z.strictObject({
  id: RuleId,
  soort: z.literal('zichtbaarBij'),
  doel: z.union([FieldId, SectionId]),
  bronVeld: FieldId,
  heeftEenVan: z.array(z.union([z.string(), z.number(), z.boolean()])).min(1),
})

/**
 * De voorwaarden van `zichtbaarAls` (besluit van 31 oktober 2026, eisenboek 12.31, ontwerp 5.6 en 5.8).
 *
 * **Welke operator op welke bron past, staat niet hier maar in de engine** (`OPERATOR_FITS` in
 * `conditions.ts`), en de poort toetst het. Een schema dat het type van de bron kende, zou bij elke
 * wijziging aan de veldcatalogus een bewaarde publicatie onleesbaar maken (D-36), dezelfde reden als bij
 * `kindFitsField`. Wat hier wel staat is welke parameter een operator draagt: een lijst waarden, één
 * waarde, een bereik of niets.
 *
 * Geen operator ten opzichte van vandaag ("binnen 30 dagen"): nu is geen invoer van de evaluator
 * (R-01e), en het formulier zou zonder handeling van de invuller veranderen (12.31, keuze 6).
 */
export const CONDITION_OPERATORS = [
  'is',
  'isNiet',
  'bevatEenVan',
  'bevatAlle',
  'bevatGeenVan',
  'isJa',
  'isNee',
  'isAkkoord',
  'isNietAkkoord',
  'gelijkAan',
  'groterDan',
  'minstens',
  'kleinerDan',
  'hoogstens',
  'tussen',
  'voor',
  'opOfVoor',
  'na',
  'opOfNa',
  'bevat',
  'ingevuld',
  'nietIngevuld',
  'minstensRijen',
  'geenRijen',
  'voldoetAanPatroon',
  'voldoetNietAanPatroon',
] as const
export const ConditionOperator = z.enum(CONDITION_OPERATORS)
export type ConditionOperator = z.infer<typeof ConditionOperator>

/** Welke parameter een operator draagt: `waarden` (een of meer), `waarde`, `waarde` met `tot`, of geen. */
export type OperatorParameter = 'waarden' | 'waarde' | 'bereik' | 'geen'

export const OPERATOR_PARAMETER: Record<ConditionOperator, OperatorParameter> = {
  is: 'waarden',
  isNiet: 'waarden',
  bevatEenVan: 'waarden',
  bevatAlle: 'waarden',
  bevatGeenVan: 'waarden',
  isJa: 'geen',
  isNee: 'geen',
  isAkkoord: 'geen',
  isNietAkkoord: 'geen',
  gelijkAan: 'waarde',
  groterDan: 'waarde',
  minstens: 'waarde',
  kleinerDan: 'waarde',
  hoogstens: 'waarde',
  tussen: 'bereik',
  voor: 'waarde',
  opOfVoor: 'waarde',
  na: 'waarde',
  opOfNa: 'waarde',
  bevat: 'waarde',
  ingevuld: 'geen',
  nietIngevuld: 'geen',
  minstensRijen: 'waarde',
  geenRijen: 'geen',
  voldoetAanPatroon: 'waarde',
  voldoetNietAanPatroon: 'waarde',
}

/** De operatoren met een patroon als waarde (eisenboek 12.43): alleen die dragen `voorbeelden`. */
export const PATTERN_OPERATORS: readonly ConditionOperator[] = ['voldoetAanPatroon', 'voldoetNietAanPatroon']

/** Of deze operator een patroon als waarde heeft. */
export function isPatternOperator(operator: ConditionOperator): boolean {
  return PATTERN_OPERATORS.includes(operator)
}

const conditionValue = z.union([z.string(), z.number()])

/**
 * Eén voorwaarde: een bronveld, een operator en wat die operator vraagt.
 *
 * Het schema eist dat de parameter er staat die de operator draagt, en geen andere. Of de waarde past
 * (een getal bij een getalbron, een datum als jjjj-mm-dd, een optie die bestaat) toetst de poort
 * (`visibilityGaps`), want een gebroken voorwaarde moet in een concept kunnen staan om haar in het
 * overzicht te laten zien: een omgezette `zichtbaarBij` met de tekst "25000" op een bedrag is gebroken,
 * en dat hoort de inrichter te zien en niet stil kwijt te raken.
 *
 * **Een patroon is de uitzondering** (eisenboek 12.43): bij `voldoetAanPatroon` en `voldoetNietAanPatroon`
 * eist het schema een geldig patroon als waarde, om dezelfde reden als bij `regexVorm`. Een patroon draait
 * in de pod en in de browser, en wat de taal van `pattern.ts` niet kent mag de opslag niet in. Alleen die
 * twee operatoren dragen `voorbeelden` (D-19); of die er zijn en kloppen toetst de poort.
 */
export const Condition = z
  .strictObject({
    bronVeld: FieldId,
    operator: ConditionOperator,
    waarden: z.array(conditionValue).min(1).optional(),
    waarde: conditionValue.optional(),
    tot: conditionValue.optional(),
    voorbeelden: PatternExamples.optional(),
  })
  .superRefine((condition, ctx) => {
    const wanted = OPERATOR_PARAMETER[condition.operator]
    const has = { waarden: condition.waarden !== undefined, waarde: condition.waarde !== undefined, tot: condition.tot !== undefined }
    const need = { waarden: wanted === 'waarden', waarde: wanted === 'waarde' || wanted === 'bereik', tot: wanted === 'bereik' }
    for (const key of ['waarden', 'waarde', 'tot'] as const) {
      if (has[key] && !need[key]) {
        ctx.addIssue({ code: 'custom', path: [key], message: `de operator ${condition.operator} draagt geen ${key}` })
      } else if (!has[key] && need[key]) {
        ctx.addIssue({ code: 'custom', path: [key], message: `de operator ${condition.operator} vraagt ${key}` })
      }
    }
    if (!isPatternOperator(condition.operator)) {
      if (condition.voorbeelden !== undefined) {
        ctx.addIssue({ code: 'custom', path: ['voorbeelden'], message: `de operator ${condition.operator} draagt geen voorbeelden` })
      }
      return
    }
    if (condition.waarde === undefined) return
    const problem = typeof condition.waarde === 'string' ? (PatternExpression.safeParse(condition.waarde).error?.issues[0]?.message) : 'een patroon is tekst'
    if (problem !== undefined) ctx.addIssue({ code: 'custom', path: ['waarde'], message: problem })
  })
export type Condition = z.infer<typeof Condition>

/** Hoe de voorwaarden samengaan: allemaal, of minstens een. Eén niveau, zonder groepen in groepen (12.31, keuze 3). */
export const Combination = z.enum(['alle', 'minstensEen'])
export type Combination = z.infer<typeof Combination>

/**
 * De zichtbaarheidsregel met voorwaarden (R-33, klasse A, besluit van 31 oktober 2026, eisenboek 12.31).
 *
 * **Naast `zichtbaarBij` en niet in zijn plaats.** Een gepubliceerde pod wordt nooit bijgewerkt (R-22a,
 * B2, D-36), en de opslagdienst leest bewaarde publicaties met het huidige `Definitie.parse`. De oude
 * soort blijft dus geldig en de evaluator blijft hem rekenen, met precies de betekenis van voorheen. Een
 * concept zet de configurator bij het openen om (`migrateVisibility` in de engine), en nieuwe regels zijn
 * altijd van deze soort.
 *
 * Zoals bij `zichtbaarBij`: geen mechanisme en geen melding (ontwerp 5.3), en het doel is een veld of een
 * sectie.
 */
const VISIBILITY_WHEN = z.strictObject({
  id: RuleId,
  soort: z.literal('zichtbaarAls'),
  doel: z.union([FieldId, SectionId]),
  combinatie: Combination,
  voorwaarden: z.array(Condition).min(1),
})

/** Alle soorten uit B11, `geheelGetal` en `zichtbaarAls`, als één gediscrimineerde unie op `soort`. */
export const Rule = z.discriminatedUnion('soort', [...HARD, ...WARNING, VISIBILITY, VISIBILITY_WHEN])
export type Rule = z.infer<typeof Rule>

/** Alleen de controles: alles behalve zichtbaarheid. */
export const Check = z.discriminatedUnion('soort', [...HARD, ...WARNING])
export type Check = z.infer<typeof Check>

export const Visibility = VISIBILITY
export type Visibility = z.infer<typeof VISIBILITY>

export const VisibilityWhen = VISIBILITY_WHEN
export type VisibilityWhen = z.infer<typeof VISIBILITY_WHEN>

/** Een zichtbaarheidsregel van een van de twee soorten. */
export type VisibilityRule = Visibility | VisibilityWhen

/** Of deze regel een zichtbaarheidsregel is, van welke soort ook (ontwerp 5.3: geen controle). */
export function isVisibilityRule(rule: Rule): rule is VisibilityRule {
  return rule.soort === 'zichtbaarBij' || rule.soort === 'zichtbaarAls'
}

/** De bronvelden van een zichtbaarheidsregel: één bij `zichtbaarBij`, een per voorwaarde bij `zichtbaarAls`. */
export function visibilitySources(rule: VisibilityRule): readonly string[] {
  return rule.soort === 'zichtbaarBij' ? [rule.bronVeld] : [...new Set(rule.voorwaarden.map((condition) => condition.bronVeld))]
}

/** De soortnamen, zodat de configurator en de engine dezelfde lijst gebruiken. */
export const RULE_KINDS = [
  'verplicht',
  'maximumTekens',
  'maximumWoorden',
  'minimumWoorden',
  'getalsbereik',
  'geheelGetal',
  'datumbereik',
  'identifierFormaat',
  'akkoordJa',
  'minimumAantal',
  'maximumAantal',
  'bestandseisen',
  'regexVorm',
  'verbodenTermen',
  'korteSectie',
  'zichtbaarBij',
  'zichtbaarAls',
] as const

export type RuleKind = (typeof RULE_KINDS)[number]
